import React from 'react';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import MetricSummaryTrendCard, {
  MetricSummaryTrendCardProps
} from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCard';
import { useMetricByWeekId, UseMetricByWeekIdArgs } from 'src/serverProxy/useMetricByWeekId';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getAppName, shouldShowNewAtlas } from 'src/utils/app';
import useDerivedMetricByWeekId from 'src/serverProxy/useDerivedMetricByWeekId';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import _get from 'lodash/get';
import { useAtlasV2MetricByWeekId } from 'src/serverProxy/useAtlasV2MetricByWeekId';
import useAtlasV2DerivedMetricByWeekId from 'src/serverProxy/useAtlasV2DerivedMetricByWeekId';

export const getCardTitle = (
  indexName: string,
  fieldName: string,
  titleOverrides?: Record<string, Record<string, string>>
) => {
  return (
    _get(titleOverrides, [indexName, fieldName]) ||
    INDEX_FIELDS.getField(getAppName(), indexName, fieldName).displayName
  );
};

export const getCardMetricType = (indexName: string, fieldName: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName).metricType;
};

export interface SmartKeyMetricCardProps extends UseMetricByWeekIdArgs {
  variant: 'large' | 'small' | 'medium';
  title?: string;
  onClick?: () => void;
  shouldSliceByWeekRange?: boolean;
  buildMetricSummaryTrendCardProps?: (defaultProps: MetricSummaryTrendCardProps) => MetricSummaryTrendCardProps;
}

/**
 * Atlas V2 Key Metric Card - it uses different hooks for fetching data - mostly market share stuff
 */
const AtlasKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps,
  shouldSliceByWeekRange,
  ...rest
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    field,
    primaryData,
    primarySlicedData,
    secondarySlicedData,
    secondaryData,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedPercentChangeLastValues,
    percentChangeLastValues
  } = useAtlasV2MetricByWeekId({
    fieldName,
    indexName,
    ...rest
  });

  // Even though content score and review rating has lasValue timeAgg function, we still want to summary the average
  const ATLAS_AVERAGE_EXCEPTION = ['reviewRating'];

  const useLatestWeek =
    field && field.timePeriodAggregationFunctionType === 'lastValue' && !ATLAS_AVERAGE_EXCEPTION.includes(field.name);

  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;

  const rawChange = useLatestWeek ? percentChangeLastValues : percentChange;
  const change = useLatestWeek ? formattedPercentChangeLastValues : formattedPercentChange;
  const primaryDataToUse = shouldSliceByWeekRange ? primarySlicedData : primaryData;
  const secondaryDataToUse = shouldSliceByWeekRange ? secondarySlicedData : secondaryData;

  const getMetricSummaryTrendCardProps = (): MetricSummaryTrendCardProps => {
    const defaultProps: MetricSummaryTrendCardProps = {
      title: title || getCardTitle(indexName, fieldName),
      splinePreviewProps: {
        metricType: getCardMetricType(indexName, fieldName),
        primaryData: primaryDataToUse,
        secondaryData: secondaryDataToUse
      },
      value: mainMetric,
      change,
      direction: getDirection(rawChange),
      selected: false,
      onClick
    };

    return buildMetricSummaryTrendCardProps ? buildMetricSummaryTrendCardProps(defaultProps) : defaultProps;
  };

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCard variant={variant} {...getMetricSummaryTrendCardProps()} />
  );
};

const AtlasV2DerivedKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps
}: SmartKeyMetricCardProps) => {
  const { isLoading, primaryData, secondaryData, formattedPrimaryTotal, formattedPercentChange, percentChange } =
    useAtlasV2DerivedMetricByWeekId(indexName, fieldName);

  const getMetricSummmaryTrendCardProps = (): MetricSummaryTrendCardProps => {
    const defaultProps: MetricSummaryTrendCardProps = {
      title: title || getCardTitle(indexName, fieldName),
      splinePreviewProps: {
        metricType: getCardMetricType(indexName, fieldName),
        primaryData,
        secondaryData
      },
      value: formattedPrimaryTotal,
      change: formattedPercentChange,
      direction: getDirection(percentChange),
      selected: false,
      onClick
    };

    return buildMetricSummaryTrendCardProps ? buildMetricSummaryTrendCardProps(defaultProps) : defaultProps;
  };

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCard variant={variant} {...getMetricSummmaryTrendCardProps()} />
  );
};

/**
 * Key metric card for a derived field
 */
const DerivedKeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps
}: SmartKeyMetricCardProps) => {
  const { isLoading, primaryData, secondaryData, formattedPrimaryTotal, formattedPercentChange, percentChange } =
    useDerivedMetricByWeekId(indexName, fieldName);

  const getMetricSummmaryTrendCardProps = (): MetricSummaryTrendCardProps => {
    const defaultProps: MetricSummaryTrendCardProps = {
      title: title || getCardTitle(indexName, fieldName),
      splinePreviewProps: {
        metricType: getCardMetricType(indexName, fieldName),
        primaryData,
        secondaryData
      },
      value: formattedPrimaryTotal,
      change: formattedPercentChange,
      direction: getDirection(percentChange),
      selected: false,
      onClick
    };

    return buildMetricSummaryTrendCardProps ? buildMetricSummaryTrendCardProps(defaultProps) : defaultProps;
  };

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCard variant={variant} {...getMetricSummmaryTrendCardProps()} />
  );
};

/**
 * Key metric card for all fields except derived
 */
const KeyMetricCard = ({
  indexName,
  fieldName,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps,
  ...rest
}: SmartKeyMetricCardProps) => {
  const {
    isLoading,
    field,
    primaryData,
    secondaryData,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedPercentChangeLastValues,
    percentChangeLastValues
  } = useMetricByWeekId({
    fieldName,
    indexName,
    ...rest
  });

  const useLatestWeek = field && field.timePeriodAggregationFunctionType === 'lastValue';
  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const rawChange = useLatestWeek ? percentChangeLastValues : percentChange;
  const change = useLatestWeek ? formattedPercentChangeLastValues : formattedPercentChange;

  const getMetricSummmaryTrendCardProps = (): MetricSummaryTrendCardProps => {
    const defaultProps: MetricSummaryTrendCardProps = {
      title: title || getCardTitle(indexName, fieldName),
      splinePreviewProps: {
        metricType: getCardMetricType(indexName, fieldName),
        primaryData,
        secondaryData
      },
      value: mainMetric,
      change,
      direction: getDirection(rawChange),
      selected: false,
      onClick
    };

    return buildMetricSummaryTrendCardProps ? buildMetricSummaryTrendCardProps(defaultProps) : defaultProps;
  };

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCard variant={variant} {...getMetricSummmaryTrendCardProps()} />
  );
};

/**
 * Given an index name and field name, it fetches the data
 * and displays it inside of a key metric summary card.
 * We call it a "smart" key metric card because it handles all
 * the logic of fetching, loading states, and display.
 */
const SmartKeyMetricCard = (props: SmartKeyMetricCardProps) => {
  const field = INDEX_FIELDS.getField(getAppName(), props.indexName, props.fieldName);

  if (shouldShowNewAtlas()) {
    return field.aggregationFunctionType === 'derived' ? (
      <AtlasV2DerivedKeyMetricCard {...props} />
    ) : (
      <AtlasKeyMetricCard {...props} />
    );
  }

  return field.aggregationFunctionType === 'derived' ? (
    <DerivedKeyMetricCard {...props} />
  ) : (
    <KeyMetricCard {...props} />
  );
};

export default SmartKeyMetricCard;
